import { Link } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { PageMeta } from "@/components/PageMeta";
import { useSiteAssets, getSiteAssetUrl } from "@/hooks/useSiteAssets";
import { useFeaturedCouponsAll } from "@/hooks/useFeaturedCoupon";
import { Tag } from "lucide-react";
import coloringShop from "@/assets/coloring-shop.png";
// Tier logos (static fallbacks)
import tierColoringColony from "@/assets/tier-coloring-with-colony.png";
import tierFluffColor from "@/assets/tier-fluff-and-color.png";
import tierFluffFoundation from "@/assets/tier-fluff-foundation.png";
import tierCultFluff from "@/assets/tier-cult-of-fluff.webp";
import customOrdersFabrics from "@/assets/custom-orders-fabrics.png";
import premadeProducts from "@/assets/premade-products.png";

const tierLogoKeys = [
  { key: "tier_logo_coloring_colony", fallback: tierColoringColony },
  { key: "tier_logo_fluff_color", fallback: tierFluffColor },
  { key: "tier_logo_fluff_foundation", fallback: tierFluffFoundation },
  { key: "tier_logo_cult_fluff", fallback: tierCultFluff },
];

const Home = () => {
  const { data: assets } = useSiteAssets();
  const featuredCoupons = useFeaturedCouponsAll();

  const tierLogos = tierLogoKeys.map((t) => getSiteAssetUrl(assets, t.key, t.fallback));

  const navItems = [
    {
      title: "Custom Shop",
      description: "Choose a curated set or build à la carte in the fabric of your choice",
      icon: null,
      image: getSiteAssetUrl(assets, "home_card_custom_orders", customOrdersFabrics),
      href: "/custom-orders",
      shopKey: "custom" as const,
      gradient: "from-brand-teal/30 to-brand-green/30",
      border: "border-brand-teal/40",
    },
    {
      title: "Premade Shop",
      description: "Ready to ship Go Fluff items",
      icon: null,
      image: getSiteAssetUrl(assets, "home_card_premade_shop", premadeProducts),
      href: "/premades",
      shopKey: "premade" as const,
      gradient: "from-brand-pink/30 to-brand-orange/30",
      border: "border-brand-pink/40",
    },
    {
      title: "Memberships",
      description: "Digital content & handmade sewn products for sugar glider lovers",
      icon: null,
      href: "/memberships",
      gradient: "from-brand-purple/30 to-brand-magenta/30",
      border: "border-brand-purple/40",
    },
    {
      title: "Coloring Shop",
      description: "Instant download sugar glider coloring pages — adult & kids printable art",
      icon: null,
      image: getSiteAssetUrl(assets, "home_card_coloring_shop", coloringShop),
      href: "/coloring-shop",
      shopKey: "coloring" as const,
      gradient: "from-[hsl(230,60%,30%)]/30 to-[hsl(270,50%,40%)]/30",
      border: "border-[hsl(250,50%,50%)]/40",
    },
  ];

  // Organization schema
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Go Fluff and Stuff",
    "url": "https://gofluffandstuff.com",
    "logo": "https://gofluffandstuff.com/logo.png",
    "description": "Handmade sugar glider cage accessories, fleece bedding sets, bonding pouches, hammocks, custom fabric orders, digital memberships, and printable coloring pages.",
    "sameAs": [
      "https://www.facebook.com/gofluffandstuff",
      "https://www.instagram.com/gofluffandstuff"
    ],
    "offers": [
      { "@type": "Offer", "name": "Custom Orders", "url": "https://gofluffandstuff.com/custom-orders" },
      { "@type": "Offer", "name": "Premade Shop", "url": "https://gofluffandstuff.com/premades" },
      { "@type": "Offer", "name": "Coloring Shop", "url": "https://gofluffandstuff.com/coloring-shop" },
      { "@type": "Offer", "name": "Memberships", "url": "https://gofluffandstuff.com/memberships" }
    ]
  };

  // FAQ schema — mirrors FAQSection.tsx questions
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do the monthly subscriptions work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "When you subscribe to a tier, you're billed on the 1st of each month via PayPal. Digital content (wallpapers, coloring pages) is available immediately in your account. Physical tiers operate on a seasonal drop schedule — you pay monthly and receive curated handmade shipments each season."
        }
      },
      {
        "@type": "Question",
        "name": "How do the seasonal drops work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fluff the Seasons and Cult of Fluff operate on a seasonal drop schedule (4 per year). You pay monthly, and once you've met the required number of payments for a season, you qualify for that drop. Each drop includes handmade cage pieces shipped directly to you."
        }
      },
      {
        "@type": "Question",
        "name": "Can I cancel my subscription?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely! You can cancel anytime from your account page. You'll continue to have access until the end of your current billing period. If you've already qualified for a seasonal drop, you'll still receive it."
        }
      },
      {
        "@type": "Question",
        "name": "Do you ship internationally?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Currently, physical seasonal drops ship within the United States. Digital-only tiers (Coloring with the Colony, Fluff and Color) are available worldwide!"
        }
      }
    ]
  };

  return (
    <MainLayout>
      <PageMeta
        title="Go Fluff and Stuff — Handmade Sugar Glider Cage Accessories"
        description="Handmade sugar glider cage sets, fleece pouches, hammocks & bedding. Monthly membership boxes, custom fabric orders, and instant-download coloring pages. Safety-first, AVL-listed materials."
        path="/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <section className="relative py-4 md:py-12 overflow-hidden min-h-screen">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-dark" />
        <div className="absolute inset-0 bg-gradient-glow" />
        
        {/* Floating orbs for visual interest */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-brand-purple/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-brand-magenta/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-brand-pink/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />
        
        <div className="container relative z-10 flex flex-col items-center text-center px-4">
          {/* Logo */}
          <div className="relative">
            <div className="absolute inset-0 bg-brand-purple/30 rounded-full blur-3xl scale-150" />
            <img 
              src="/logo.png" 
              alt="Go Fluff and Stuff - Sugar Glider Logo"
              className="relative w-20 md:w-44 h-auto mb-3 animate-float drop-shadow-2xl"
              width={176}
              height={176}
              fetchPriority="high"
            />
          </div>
          
          {/* Title */}
          <h1 className="text-2xl md:text-5xl font-bold text-foreground mb-2">
            Welcome to{" "}
            <span className="text-rainbow-gradient font-display">Go Fluff and Stuff</span>
          </h1>
          
          <p className="text-sm md:text-lg text-muted-foreground max-w-xl mb-4 leading-snug">
            Handmade sugar glider accessories, digital memberships, and custom fabric orders.
          </p>
          
          {/* Navigation Cards */}
          <div className="grid grid-cols-2 gap-2.5 sm:gap-4 w-full max-w-3xl mb-8">
            {navItems.map((item) => (
              <Link
                key={item.title}
                to={item.href}
                className={`
                  group relative rounded-2xl overflow-hidden
                  bg-gradient-to-br ${item.gradient}
                  border ${item.border}
                  backdrop-blur-sm
                  transition-all duration-300
                  hover:scale-[1.02] hover:shadow-lg hover:shadow-brand-purple/20
                  active:scale-[0.98]
                  p-3 sm:p-6 text-left
                `}
              >
                <div className="flex flex-col items-center text-center gap-2 sm:flex-row sm:items-start sm:text-left sm:gap-4">
                  {item.icon ? (
                    <div className="p-3 rounded-xl bg-background/50 backdrop-blur-sm">
                      <item.icon className="h-6 w-6 text-foreground" />
                    </div>
                  ) : item.image ? (
                    <div className="w-12 h-12 rounded-xl bg-background/50 backdrop-blur-sm overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt="" 
                        className="w-full h-full object-cover"
                        width={48}
                        height={48}
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-1 p-1.5 rounded-xl bg-background/50 backdrop-blur-sm flex-shrink-0">
                      {tierLogos.map((tierLogo, idx) => (
                        <img 
                          key={idx} 
                          src={tierLogo} 
                          alt="" 
                          className="w-6 h-6 rounded-full object-cover aspect-square"
                          width={24}
                          height={24}
                          loading="lazy"
                        />
                      ))}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h2 className="text-sm sm:text-lg font-semibold text-foreground mb-1 leading-tight">
                      {item.title}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-snug whitespace-pre-line">
                      {item.description}
                    </p>
                    {item.shopKey && (featuredCoupons as any)[item.shopKey] && (
                      <div className="flex items-center justify-center sm:justify-start gap-1 mt-1.5">
                        <Tag className="h-3 w-3 text-brand-purple" />
                        <span className="text-xs font-medium text-brand-purple">Coupon available!</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </MainLayout>
  );
};

export default Home;
